import { Router } from "express";
import { db } from "@workspace/db";
import {
  tendersTable, companiesTable, subscriptionsTable, invoicesTable, analysesTable, plansTable,
} from "@workspace/db";
import { eq, and, gte, lte, desc, count, sql } from "drizzle-orm";
import { requireAuth, requireAdmin, type AuthRequest } from "../lib/auth";

// Simple in-memory cache for dashboard stats (5-minute TTL)
const dashboardCache = new Map<string, { data: any; expiresAt: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const router = Router();

// Public (unauthenticated) platform metrics — used on landing page
router.get("/stats/public", async (_req, res, next) => {
  try {
    const [companiesResult, analysesResult, tendersResult] = await Promise.all([
      db.select({ count: count() }).from(companiesTable),
      db.select({ count: count() }).from(analysesTable),
      db.select({ count: count() }).from(tendersTable),
    ]);
    res.json({
      totalCompanies: Number(companiesResult[0]?.count ?? 0),
      totalAnalyses: Number(analysesResult[0]?.count ?? 0),
      totalTenders: Number(tendersResult[0]?.count ?? 0),
    });
  } catch (err) {
    next(err);
  }
});

router.get("/stats/dashboard", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const companyId = req.companyId;
    if (!companyId) return res.json({
      totalTenders: 0, analyzing: 0, recommendedBid: 0, rejected: 0,
      analysisUsed: 0, analysisLimit: 0, recentTenders: [],
    });

    // Check cache first
    const cacheKey = `dashboard-${companyId}`;
    const cached = dashboardCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
      return res.json(cached.data);
    }

    const [totalResult, analyzingResult, bidResult, noBidResult, company, recentTenders, activePlan] =
      await Promise.all([
        db.select({ count: count() }).from(tendersTable).where(eq(tendersTable.companyId, companyId)),
        db.select({ count: count() }).from(tendersTable)
          .where(and(eq(tendersTable.companyId, companyId), eq(tendersTable.status, "analyzing"))),
        db.select({ count: count() }).from(tendersTable)
          .where(and(eq(tendersTable.companyId, companyId), eq(tendersTable.status, "bid"))),
        db.select({ count: count() }).from(tendersTable)
          .where(and(eq(tendersTable.companyId, companyId), eq(tendersTable.status, "no_bid"))),
        db.select().from(companiesTable).where(eq(companiesTable.id, companyId)).limit(1),
        db.select().from(tendersTable)
          .where(eq(tendersTable.companyId, companyId))
          .orderBy(desc(tendersTable.createdAt))
          .limit(5),
        db.select({ analysisLimit: plansTable.analysisLimit })
          .from(subscriptionsTable)
          .innerJoin(plansTable, eq(subscriptionsTable.planId, plansTable.id))
          .where(and(eq(subscriptionsTable.companyId, companyId), eq(subscriptionsTable.status, "active")))
          .limit(1),
      ]);

    const result = {
      totalTenders: Number(totalResult[0]?.count ?? 0),
      analyzing: Number(analyzingResult[0]?.count ?? 0),
      recommendedBid: Number(bidResult[0]?.count ?? 0),
      rejected: Number(noBidResult[0]?.count ?? 0),
      analysisUsed: company[0]?.analysisUsedThisMonth ?? 0,
      analysisLimit: activePlan[0]?.analysisLimit ?? 5,
      recentTenders,
    };

    // Cache the result
    dashboardCache.set(cacheKey, {
      data: result,
      expiresAt: Date.now() + CACHE_TTL,
    });

    res.json(result);
  } catch (err) {
    next(err);
  }
});

router.get("/stats/tenders-by-status", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const companyId = req.companyId;
    if (!companyId) return res.json([]);

    const result = await db
      .select({ status: tendersTable.status, count: count() })
      .from(tendersTable)
      .where(eq(tendersTable.companyId, companyId))
      .groupBy(tendersTable.status);

    res.json(result.map((r) => ({ status: r.status, count: Number(r.count) })));
  } catch (err) {
    next(err);
  }
});

router.get("/stats/upcoming-deadlines", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const companyId = req.companyId;
    if (!companyId) return res.json([]);

    const now = new Date();
    const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    const tenders = await db
      .select()
      .from(tendersTable)
      .where(
        and(
          eq(tendersTable.companyId, companyId),
          gte(tendersTable.deadline, now),
          lte(tendersTable.deadline, sevenDaysFromNow),
        ),
      )
      .orderBy(tendersTable.deadline)
      .limit(10);

    res.json(tenders);
  } catch (err) {
    next(err);
  }
});

router.get("/admin/revenue", requireAdmin, async (req: AuthRequest, res, next) => {
  try {
    const [activeSubs, totalCompanies] = await Promise.all([
      db.select({ count: count() }).from(subscriptionsTable).where(eq(subscriptionsTable.status, "active")),
      db.select({ count: count() }).from(companiesTable),
    ]);

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const revenueResult = await db
      .select({ total: sql<string>`sum(cast(${invoicesTable.totalAmount} as decimal))` })
      .from(invoicesTable)
      .where(
        and(
          eq(invoicesTable.status, "paid"),
          gte(invoicesTable.createdAt, thirtyDaysAgo),
        ),
      );

    res.json({
      mrr: Number(revenueResult[0]?.total ?? 0),
      activeSubscriptions: Number(activeSubs[0]?.count ?? 0),
      totalCompanies: Number(totalCompanies[0]?.count ?? 0),
      churnThisMonth: 0,
      revenueThisMonth: Number(revenueResult[0]?.total ?? 0),
    });
  } catch (err) {
    next(err);
  }
});

export default router;
